import {Component, computed, EventEmitter, Input, OnChanges, OnInit, Output, signal, SimpleChanges} from '@angular/core';
import {IRole} from "../../../interfaces/IRole";
import {ICharity} from "../../../interfaces/charity/ICharity";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {PermissionsService} from "../../../services/permissions.service";
import {CharityService} from "../../../services/charity.service";
import {ICharitiesByCategoryResult} from "../../../interfaces/charity/ICharitiesByCategoryResult";
import {MatCardModule} from "@angular/material/card";
import {MatButtonModule} from "@angular/material/button";
import {MatSelectModule} from "@angular/material/select";
import {MatFormFieldModule} from "@angular/material/form-field";
import {CommonModule} from "@angular/common";
import {MatRadioModule} from "@angular/material/radio";
import {FormsModule} from "@angular/forms";
import {MatIconModule} from "@angular/material/icon";
import {MatChipsModule} from "@angular/material/chips";
import {IAdminQueryResult} from "../../../interfaces/IAdminQueryResult";
import {IAddNewRoleCommand} from "../../../interfaces/IAddNewRoleCommand";
import {SnackbarService} from "../../../services/snackbar.service";
import {GameService} from "../../../services/game.service";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";

@Component({
  selector: 'app-add-user-roles',
  standalone: true,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatSelectModule,
    MatFormFieldModule,
    CommonModule,
    MatRadioModule,
    FormsModule,
    MatIconModule,
    MatChipsModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './add-user-roles.component.html',
  styleUrl: './add-user-roles.component.scss'
})
export class AddUserRolesComponent implements OnInit, OnChanges {
  @Input() targetUser: IAdminQueryResult | null = null;
  @Output() rolesAddedSuccessfully = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  // Signal wrapper for targetUser input (so computed signals react to changes)
  private targetUserSignal = signal<IAdminQueryResult | null>(null);

  // State signals
  selectedRoleType = signal<'game' | 'cross-game' | 'charity' | null>(null);
  selectedGame = signal<IGameQueryResult | null>(null);
  selectedRoles = signal<IRole[]>([]);
  selectedCharities = signal<ICharity[]>([]);
  isLoading = signal(false);

  // Cached API data (loaded once)
  private allCrossGameRoles = signal<IRole[]>([]);

  // Non-reactive state
  availableGames: IGameQueryResult[] = this.gameService.getAllAvailableGames();
  availableGameRoles: IRole[] = [];
  availableCharities: ICharity[] = [];

  // Computed signals - automatically update when dependencies change
  needsGameSelectionBoolean = computed(() =>
    this.selectedRoleType() === 'game' || this.selectedRoleType() === 'charity'
  );

  canSaveRoles = computed(() => {
    if (this.isLoading()) return false;

    const roleType = this.selectedRoleType();

    if (roleType === 'cross-game') {
      return this.selectedRoles().length > 0;
    }

    if (!this.selectedGame()) return false;

    if (roleType === 'charity') {
      return this.selectedCharities().length > 0;
    }

    if (roleType === 'game') {
      return this.selectedRoles().length > 0;
    }

    return false;
  });

  // Filters cached cross-game roles based on what the user already has
  availableCrossGameRoles = computed(() => {
    const currentRoleIds = this.getCurrentUserCrossGameRoleIds();
    return this.allCrossGameRoles().filter(role => !currentRoleIds.includes(role.Id));
  });

  constructor(
    private permissionsService: PermissionsService,
    private charityService: CharityService,
    private snackBarService: SnackbarService,
    private gameService: GameService
  ) {
  }

  ngOnInit() {
    this.loadCrossGameRoles();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Sync the signal wrapper when the input changes
    if (changes['targetUser']) {
      this.targetUserSignal.set(this.targetUser);
    }
  }

  onRoleTypeChange(newType: 'game' | 'cross-game' | 'charity' | null) {
    this.selectedRoleType.set(newType);
    this.selectedRoles.set([]);
    this.selectedCharities.set([]);
    this.selectedGame.set(null);
    this.availableGameRoles = [];
    this.availableCharities = [];
  }

  onGameChange(game: IGameQueryResult | null) {
    this.selectedGame.set(game);
    this.selectedRoles.set([]);
    this.selectedCharities.set([]);
    this.availableGameRoles = [];
    this.availableCharities = [];

    if (game) {
      if (this.selectedRoleType() === 'game') {
        this.loadGameRoles();
      } else if (this.selectedRoleType() === 'charity') {
        this.loadCharities();
      }
    }
  }

  loadGameRoles() {
    const game = this.selectedGame();
    if (!game) return;

    this.permissionsService.fetchPossibleRoles(game.Id).subscribe({
      next: (roles) => {
        this.availableGameRoles = roles.filter(role => !role.IsCrossGameRole);
      }
    });
  }

  loadCrossGameRoles() {
    this.permissionsService.fetchPossibleRoles('').subscribe({
      next: (roles) => {
        // Store all cross-game roles - filtering happens in the computed signal
        this.allCrossGameRoles.set(roles.filter(role => role.IsCrossGameRole));
      }
    });
  }

  private getCurrentUserCrossGameRoleIds(): string[] {
    const user = this.targetUserSignal();
    if (!user || !user.Roles) {
      return [];
    }

    // Extract all cross-game roles from the user's roles
    const crossGameRoles: IRole[] = [];
    user.Roles.forEach(roleGroup => {
      const crossGameRolesInGroup = roleGroup.GameRoles.filter(role => role.IsCrossGameRole);
      crossGameRoles.push(...crossGameRolesInGroup);
    });

    return crossGameRoles.map(role => role.Id);
  }

  loadCharities() {
    const game = this.selectedGame();
    if (!game) return;

    this.charityService.fetchCharitiesPerGameByCategory(game.Id).subscribe({
      next: (charitiesByCategory: ICharitiesByCategoryResult[]) => {
        this.availableCharities = charitiesByCategory.flatMap(category => category.Charities);
      }
    });
  }

  onSave() {
    if (!this.targetUser) {
      this.snackBarService.openErrorSnackBar("Invalid user.");
      return;
    }

    const roleType = this.selectedRoleType();
    const roles = this.selectedRoles();
    const charities = this.selectedCharities();
    const game = this.selectedGame();

    // Handle charity admin assignment
    if (roleType === 'charity') {
      if (charities.length === 0) {
        this.snackBarService.openErrorSnackBar("Please select at least one charity.");
        return;
      }
      // TODO: Implement charity admin assignment API endpoint
      this.snackBarService.openErrorSnackBar("Charity admin assignment feature is coming soon. Please contact a system administrator.");
      return;
    }

    // Handle role assignment (game or cross-game)
    if (roles.length === 0) {
      this.snackBarService.openErrorSnackBar("Please select at least one role.");
      return;
    }

    this.isLoading.set(true);

    const payload: IAddNewRoleCommand = {
      targetAdminId: this.targetUser.Id,
      roleIds: roles.map(role => role.Id),
      gameId: roleType === 'cross-game' ? '00000000-0000-0000-0000-000000000000' : game?.Id,
      adminEmail: this.targetUser.Email,
      adminFirstName: this.targetUser.FirstName,
      adminLastName: this.targetUser.LastName,
      gameName: game?.Name ?? '',
      redirectUrl: `${window.location.origin}/login`
    };

    this.permissionsService.addNewUserRole(payload).subscribe({
      next: () => {
        this.snackBarService.openSuccessfulSnackBar('Successfully added new role(s)');
        this.isLoading.set(false);
        this.rolesAddedSuccessfully.emit();
      },
      error: () => {
        this.isLoading.set(false);
        this.snackBarService.openErrorSnackBar('Failed to add new role(s). Please try again or contact a system admin');
      }
    });
  }

  onCancel() {
    this.cancelled.emit();
  }

  removeRole(roleToRemove: IRole) {
    this.selectedRoles.update(roles => roles.filter(r => r.Id !== roleToRemove.Id));
  }

  removeCharity(charityToRemove: ICharity) {
    this.selectedCharities.update(charities => charities.filter(c => c.Id !== charityToRemove.Id));
  }
}
